import { api } from '@/api/client';
export function enablePushSubscription(subscription: PushSubscriptionJSON) {
  return api.post('/api/v2/push/subscriptions', subscription);
}
export function disablePushSubscription(endpoint: string) {
  return api.delete(`/api/v2/push/subscriptions?endpoint=${encodeURIComponent(endpoint)}`);
}
import type { AssemblyLine, AssemblyWorker, CarrierCompany, Role, Shop, SystemSettings, UserRow } from './types';

export function saveSystemSettings(payload: { expected: SystemSettings; next: SystemSettings }) {
  return api.patch<SystemSettings>('/api/v2/settings', payload);
}

export function createCarrier(payload: { type: CarrierCompany['type']; name: string; code: string; websiteUrl: string; trackingUrl: string }) {
  return api.post<CarrierCompany>('/api/v2/carriers', payload);
}

export function updateCarrier(id: string, payload: { name: string; code: string; websiteUrl: string; trackingUrl: string; isActive: boolean; expectedUpdatedAt: string }) {
  return api.patch<CarrierCompany>(`/api/v2/carriers/${encodeURIComponent(id)}`, payload);
}

export function reorderCarriers(payload: { type: CarrierCompany['type']; ids: string[]; expected: Array<{ id: string; updatedAt: string }> }) {
  return api.post<{ success: boolean }>('/api/v2/carriers/reorder', payload);
}

export function importCarriers(form: FormData) {
  return api.postForm<{ id: string; totalRows: number; successRows: number; failedRows: number; createdRows: number; updatedRows: number; skippedRows: number; errors: Array<{ rowNumber: number; message: string; rowData: Record<string, string> }>; replay: boolean }>('/api/v2/import/carriers', form);
}

export function createShop(name: string) { return api.post<Shop>('/api/v2/shops', { name }); }
export function renameShop(id: string, name: string, expectedUpdatedAt: string) { return api.patch<Shop>(`/api/v2/shops/${encodeURIComponent(id)}`, { name, expectedUpdatedAt }); }
export function updateShop(id: string, payload: { name: string; isActive: boolean; expectedUpdatedAt: string }) { return api.patch<Shop>(`/api/v2/shops/${encodeURIComponent(id)}`, payload); }
export function deleteShop(id: string) { return api.delete<{ id: string }>(`/api/v2/shops/${encodeURIComponent(id)}`); }
export function setDefaultShop(shopId: string, expectedShopName: string) { return api.patch<{ defaultShopName: string }>('/api/v2/shops/default', { shopId, expectedShopName }); }

export function cleanupAuditLogs(payload: { phrase: string; cutoff: string; expectedCount: number }) {
  return api.post<{ deletedCount: number }>('/api/v2/audit-logs/cleanup', payload);
}

export function createRoleFromTemplate(payload: {
  code: string;
  name: string;
  description: string;
  baseRole: Role['baseRole'];
  permissions: string[];
  restrictions: string[];
  warehouseIds?: string[] | null;
}) {
  return api.post<Role>('/api/v2/roles', payload);
}

export function saveRolePermissions(id: string, payload: {
  permissions: string[];
  restrictions: string[];
  warehouseIds: string[] | null;
  reason: string;
  expectedUpdatedAt: string;
}) {
  return api.patch<Role>(`/api/v2/roles/${encodeURIComponent(id)}`, payload);
}

export function createAccount(payload: { username: string; name: string; role: string; password: string; isActive: boolean; passwordMode?: 'temporary' | 'link' }) {
  return api.post<UserRow & { setupToken?: string; setupExpiresAt?: string }>('/api/v2/users', payload);
}

export function updateAccount(id: string, payload: { name?: string; role?: string; isActive?: boolean; password?: string }) {
  return api.patch<UserRow>(`/api/v2/users/${encodeURIComponent(id)}`, payload);
}

export function deleteRole(id: string) {
  return api.delete<{ success: boolean }>(`/api/v2/roles/${encodeURIComponent(id)}`);
}

export function createAssemblyLine(payload: { name: string; sortOrder: number; workerIds: string[] }) {
  return api.post<AssemblyLine>('/api/v2/assembly-lines', payload);
}

export function createAssemblyWorker(payload: { name: string; assemblyLineId: string | null }) {
  return api.post<AssemblyWorker>('/api/v2/assembly-workers', payload);
}

export function updateAssemblyLine(id: string, payload: { name: string; sortOrder: number; isActive: boolean; workerIds: string[]; expectedWorkerIds: string[]; expectedUpdatedAt: string }) {
  return api.patch<AssemblyLine>(`/api/v2/assembly-lines/${encodeURIComponent(id)}`, payload);
}

export function deleteAssemblyLine(id: string) { return api.delete<{ id: string }>(`/api/v2/assembly-lines/${encodeURIComponent(id)}`); }

export function saveWarehouse(id: string | undefined, payload: { code: string; name: string; address: string; sortOrder: number }) {
  return id ? api.patch(`/api/v2/warehouses/${encodeURIComponent(id)}`, payload) : api.post('/api/v2/warehouses', payload);
}
export function removeWarehouse(id: string) { return api.delete(`/api/v2/warehouses/${encodeURIComponent(id)}`); }
export function defaultInboundWarehouse(id: string) { return api.post(`/api/v2/warehouses/${encodeURIComponent(id)}/default-inbound`, {}); }
export function reorderWarehouses(ids: string[]) { return api.post('/api/v2/warehouses/reorder', { ids }); }
export function cleanupUnusedUploads() { return api.delete<{ deleted: number; bytes: number }>('/api/v2/uploads/maintenance?cleanup=unreferenced'); }
